import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Modal from "react-modal";
import { toggleLoading, setAlert } from "../../../state/creators";
import { ApiResultModel } from "../../../_models/api.result.model";
import { StandardButtonAnchor } from "../../../styles/type/buttons";
import { PermissionsValidate, UpdateFilter } from "../../../functions/helpers";
import { Permissions } from "../../../_enums/permissions.enum";
import { AlertType } from "../../../state/reducers/setAlert";
import { IHttpResponse } from "../../../functions/http-functions/http";
import { useAuth } from "../../../state/context/auth.store";
import { ModalStyles } from "../../../styles/modals";
import { IBranch } from "../../../_models/data.branch.model";
import { AssignUserBranches, GetUserBranches, UnassignUserBranches } from "../../../functions/http-functions/branches";
import { UserInterface } from "../../../_types/user";
import { DefaultPaginationModel } from "../../../_models/data.defaultPagination.model";
import { ListingModel } from "../../../_models/data.listingResult.model";
import { FilterModel } from "../../../_models/data.filter.model";
import Table from "../../table";
import ButtonIcon from "../../icons/buttons";
import { TableOptionsStyled } from "../../../styles/tables/options";
import { Note } from "../../../styles/forms/inputs";
import AssignUserBranchesModel from "../../modals/assignUserBranchesModel";

type Props = {
	user: UserInterface;
	branches: IBranch[];
	total: number;
	toggleLoading: (loading: boolean) => void;
	setAlert: (alert: AlertType) => void;
};

export interface IUserBranches {
	branches: string[];
}

const UserUsersForm: React.FC<Props> = ({ user, branches, total, toggleLoading, setAlert }) => {
	const { auth } = useAuth();
	const [assignModalIsOpen, setAssignModalIsOpen] = useState<boolean>(false);

	const [{ resultItems, pageNo, pageSize, paginationItems, totalRecords, resultItemsAll, filters, order }, setState] = useState({
		...new DefaultPaginationModel(),
		...{
			totalRecords: total,
			resultItemsAll: [],
			filters: [],
			order: null,
		},
	});

	useEffect(() => {
		setState((prevState) => ({
			...prevState,
			...new ListingModel({
				pageNo,
				pageSize,
				resultItems: branches.map((item) => ({
					...item,
					...{
						"address.postcode": item.address ? item.address.postcode : null,
						"address.county": item.address ? item.address.county : null,
					},
				})),
				resultItemsAll,
				totalRecords: total,
			}),
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updatePage = async (type, config?, tableFilter?, orderFilter?) => {
		const pageNoValue = type === "reset" || type === "start" ? 1 : config?.pageNo || pageNo;
		const pageSizeValue = config?.pageSize || pageSize;

		const FilterValues = await UpdateFilter(type, {
			pageNo: pageNoValue,
			pageSize: pageSizeValue,
		});

		if (tableFilter) {
			setState((prevState) => ({
				...prevState,
				...{
					filters: tableFilter,
				},
			}));
		}

		if (orderFilter) {
			setState((prevState) => ({
				...prevState,
				...{
					order: orderFilter,
				},
			}));
		}

		filter(FilterValues, tableFilter || filters, orderFilter || order);
	};

	const filter = async (FilterValues, TableFilter, OrderFilter) => {
		try {
			toggleLoading(true);
			const res = await GetUserBranches(auth.token, user._id, new FilterModel(FilterValues, TableFilter, OrderFilter));
			setState((prevState) => ({
				...prevState,
				...FilterValues,
				...new ListingModel(
					{
						resultItems: res.data.map((item) => ({
							...item,
							...{
								"address.postcode": item.address ? item.address.postcode : null,
								"address.county": item.address ? item.address.county : null,
							},
						})),
						totalRecords: res.total,
					},
					FilterValues
				),
			}));
		} catch (err: unknown) {
			const error = err as IHttpResponse;
			const ErrorResult = new ApiResultModel(error);
			setAlert({ type: "error", message: ErrorResult.message });
		} finally {
			toggleLoading(false);
		}
	};

	const assignBranches = async (branches: string[]) => {
		try {
			toggleLoading(true);
			await AssignUserBranches(auth.token, user._id, {
				branches,
			});
			updatePage("reset");
			setAlert({ message: "Branches assigned successfully" });
		} catch (err: unknown) {
			const error = err as IHttpResponse;
			const ErrorResult = new ApiResultModel(error);
			setAlert({ type: "error", message: ErrorResult.message });
		} finally {
			setAssignModalIsOpen(false);
			toggleLoading(false);
		}
	};

	const unassignBranches = async (e: MouseEvent, branch: IBranch) => {
		try {
			toggleLoading(true);
			await UnassignUserBranches(auth.token, user._id, {
				branches: [branch._id],
			});
			updatePage("reset");
			setAlert({ message: "Team unassigned successfully" });
		} catch (err: unknown) {
			const error = err as IHttpResponse;
			const ErrorResult = new ApiResultModel(error);
			setAlert({ type: "error", message: ErrorResult.message });
		} finally {
			setAssignModalIsOpen(false);
			toggleLoading(false);
		}
	};

	const TableProperties: { navigate?: any; columns?: any } = {
		columns: [
			{
				Name: "Name",
				Value: "name",
				Type: "String",
				Filter: "Search",
				Order: "String",
			},
			{
				Name: "Users",
				Value: "userCount",
				Type: "String",
			},
		],
	};

	if (PermissionsValidate(auth.token, [Permissions.CanManageCustomer])) {
		TableProperties.columns.push({
			Name: null,
			Value: "_id",
			Filter: null,
			ReturnType: "item",
			ReturnTypeAction: unassignBranches,
			Text: "Remove",
		});
	}

	return (
		<>
			{PermissionsValidate(auth.token, [Permissions.CanManageCustomer]) ? (
				<TableOptionsStyled>
					<StandardButtonAnchor onClick={() => setAssignModalIsOpen(true)}>
						<ButtonIcon type="add" />
						Assign Team
					</StandardButtonAnchor>
				</TableOptionsStyled>
			) : null}
			<Note>
				Teams offer a way to group users for more focused organisation and granular reporting, although they are optional as all users are automatically added to your default organisation
				team.
			</Note>
			<Table
				resultItems={resultItems}
				tableProperties={TableProperties}
				pageNo={pageNo}
				pageSize={pageSize}
				UpdatePage={updatePage}
				paginationItems={paginationItems}
				totalRecords={totalRecords}
				scaleTable={true}
			/>
			{assignModalIsOpen ? (
				<Modal isOpen={assignModalIsOpen} onRequestClose={() => setAssignModalIsOpen(false)} style={ModalStyles} contentLabel="" shouldCloseOnOverlayClick={true} ariaHideApp={false}>
					<AssignUserBranchesModel
						name={`${user?.firstName} ${user?.lastName}`}
						confirmButtonAction={assignBranches}
						closeModal={() => setAssignModalIsOpen(false)}
						filteredBranches={resultItems.map((branch: IBranch) => branch._id) ?? []}
					/>
				</Modal>
			) : null}
		</>
	);
};

const mapDispatchToProps = {
	toggleLoading,
	setAlert,
};

export default connect(null, mapDispatchToProps)(UserUsersForm);
